/**
 * ItinerarySyncReporter — pushes the AUTHORITATIVE itinerary state up to
 * the host page (wg:event → bootstrap → WunderGuide.on('event')).
 *
 * Deep-integration hosts (explore map) render a trip-count chip and mark
 * added pins. Counting host-side (localStorage) drifts from the real
 * itinerary the moment the guest session differs across devices or an add
 * is gated/dropped — the widget owns the store, so the widget reports.
 * Payload carries item titles (the cross-surface join key, same as
 * itinerary_hover) plus the registration-gated QUEUED titles so the host
 * can keep "added" affordances consistent through the register flow and
 * across refreshes. Fired on every change (including the initial hydrate),
 * so hosts self-heal stale state.
 */
import { useEffect } from 'react';
import { useItinerary } from '../context/ItineraryContext';
import { useParentBridge } from '../context/ParentBridgeContext';

export default function ItinerarySyncReporter() {
  const { items, pendingAdds, hydrated, shareToken } = useItinerary();
  const parentBridge = useParentBridge();

  useEffect(() => {
    const pending = items.filter(
      it =>
        it.status === 'pending_pay' ||
        ((it.status === 'saved' || it.status === 'suggested') &&
          it.embedded_url),
    ).length;
    parentBridge.send({
      type: 'wg:event',
      payload: {
        event_type: 'itinerary_updated',
        // True once the SERVER copy was fetched. Without it a host cannot tell an
        // honestly-empty trip from a widget that has not loaded yet — which forced
        // hosts to ignore empty reports, which meant a trip emptied elsewhere
        // resurrected from their stale localStorage.
        hydrated,
        count: items.length,
        pending_booking: pending,
        // Read-only share handle, present once the owner mints a link (or a
        // returning owner hydrates an already-shared trip). Lets a host (explore)
        // build the ?trip=<token> share URL. Absent = not shared.
        share_token: shareToken,
        items: items.map(it => ({
          title: it.title,
          status: it.status,
          // Cross-surface stable key (idempotent add/remove; product owner
          // 2026-07-09): explore reconciles pins by these, not titles.
          activity_id: it.activity_id,
          provider: it.provider,
          // Visitor emphasis so hosts (explore) can style must-do/maybe pins
          // and default a shared read-only view to must-do items.
          priority: it.priority,
        })),
        queued: pendingAdds.map(p => p.title),
      },
    });
    // parentBridge.send identity is stable enough; items/queue trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, pendingAdds, hydrated, shareToken]);

  return null;
}
